import { useEffect, useState } from 'react';

import Lottie from 'lottie-react';
import { motion } from 'framer-motion';
import { IoClose } from 'react-icons/io5';

import confetti from '../../../confetti.json';
import { useTranslation } from 'react-i18next';
import newLoader from '../../../assets/newLoader.json';
import { useCheckout } from '../../../hooks/use-checkout';
import { usePaymentState } from '../hooks/use-payment-state';
import { useAppConfig } from '../../../hooks/use-app-config';
import { getQueryParam } from '../../../utils/get-query-param';
import { getRedirectUrlWithEventParams } from '../../../utils/url-utils';
import { useCheckoutFlowType } from '../hooks/use-checkout-flow-type';
import { RedirectStatus, BrowserType } from '../../../constants/enums';
import { SUPPORTED_BROWSERS_FOR_AUTO_REDIRECT } from '../../../constants/consts';

const browserType = getQueryParam('browserType');

const IS_BROWSER_AUTO_REDIRECT_SUPPORTED = SUPPORTED_BROWSERS_FOR_AUTO_REDIRECT.includes(
  browserType as BrowserType,
);

export function SuccessMessage({ handleClose }: { handleClose: () => void }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const publisherPurchaseId = usePaymentState((state) => state.publisherPurchaseId);
  const { featureFlags } = useAppConfig();
  const { redirectUrl, getEventParams } = useCheckout();
  const [formattedRedirectUrl, setFormattedRedirectUrl] = useState('');
  const { isPaymentLinksFlow } = useCheckoutFlowType();

  const shouldHideSuccessScreen =
    isPaymentLinksFlow && featureFlags?.checkout_disable_success_screen;

  const showBackToGameButtonFlag = featureFlags?.checkout_show_back_to_game_button;
  const displayBackToGameButton =
    isPaymentLinksFlow && (showBackToGameButtonFlag || !IS_BROWSER_AUTO_REDIRECT_SUPPORTED);

  const shouldAutoRedirect =
    isPaymentLinksFlow && IS_BROWSER_AUTO_REDIRECT_SUPPORTED && !showBackToGameButtonFlag;

  const [isRedirectAttempted, setIsRedirectAttempted] = useState(!shouldAutoRedirect);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (redirectUrl) {
      const eventParams = getEventParams();
      const redirectUrlWithEventParams = getRedirectUrlWithEventParams(
        redirectUrl,
        RedirectStatus.SUCCESS,
        { ...eventParams, purchaseId: publisherPurchaseId },
      );
      if (shouldAutoRedirect) {
        window.location.href = redirectUrlWithEventParams;
        timeout = setTimeout(() => {
          setIsRedirectAttempted(true);
        }, 1000);
      }
      setFormattedRedirectUrl(redirectUrlWithEventParams);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [redirectUrl, publisherPurchaseId, shouldAutoRedirect, getEventParams]);

  if (shouldHideSuccessScreen) {
    return null;
  }

  return (
    <div className="success-wrapper">
      <section className="animation-wrapper">
        <Lottie animationData={confetti} loop={false} size={100} />
      </section>
      <section className="success">
        {!featureFlags?.checkout_deeplinks_disable_store && !redirectUrl && (
          <div
            className="close-icon"
            onClick={() => {
              if (redirectUrl) {
                window.location.href = getRedirectUrlWithEventParams(
                  redirectUrl,
                  RedirectStatus.CANCEL,
                  getEventParams(),
                );
              } else {
                handleClose();
              }
            }}
          >
            <IoClose size={24} color="white" />
          </div>
        )}
        {isPaymentLinksFlow ? (
          <>
            <motion.h1 data-testid="checkout_success_header">{t('completed')}</motion.h1>
            <motion.h4 data-testid="checkout_success_desc">{t('paymentProcessed')}</motion.h4>
            {displayBackToGameButton || isRedirectAttempted ? (
              <button
                className="redirect-button"
                style={{
                  marginTop: '1em',
                  maxWidth: '200px',
                  width: 'fit-content',
                  zIndex: 100000000,
                }}
                onClick={() => (window.location.href = formattedRedirectUrl)}
              >
                {t('returnToGame')}
              </button>
            ) : (
              <Lottie animationData={newLoader} style={{ height: 60 }} />
            )}
          </>
        ) : (
          <>
            <motion.h1 data-testid="checkout_success_header">{t('completed')}</motion.h1>
            <motion.h4 data-testid="checkout_success_desc">
              {t('yourPurchaseWasSuccessful')}
            </motion.h4>
          </>
        )}
      </section>
    </div>
  );
}
